import React, { useState, useEffect } from 'react'
import styled from 'styled-typed'
import { ActionButton } from 'uiComponents/buttons'
import { ModalDialog, ModalDialogTitle, NavigationSection } from 'uiComponents/popups/modal'
import { ChartDataLoader } from 'uiComponents/loaders'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { TextInput } from 'uiComponents/input'
import { useTranslation } from 'translations'

const ErrorMessage = styled(ValidationMessage)`
    font-size: 0.8em;
    top: 0em;
`

interface DialogProps {
    accountSlug: string
    showErrors: boolean
    duplicateSlug: boolean
    loading: boolean
    onCancel: () => void
    onConfirm: (name: string, slug: string) => void
}

export function AddResellerDialog(props: DialogProps) {
    const { t } = useTranslation()
    const [name, setName] = useState<string>('')
    const [slug, setSlug] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [slugError, setSlugError] = useState<boolean>(false)
    const [autoSlug, setAutoSlug] = useState<boolean>(true)

    useEffect(() => {
        if (props.showErrors) {
            checkForErrors()
        }
    }, [props.showErrors])

    useEffect(() => {
        if (props.duplicateSlug) {
            setSlugError(true)
        }
    }, [props.duplicateSlug])

    const checkForErrors = () => {
        setNameError(!name.trim())
        setSlugError(!slug.trim())
    }

    const onSave = () => {
        const formValid = !!name.trim() && !!slug.trim()
        if (formValid) {
            props.onConfirm(name.trim(), slug.trim())
        } else {
            checkForErrors()
        }
    }

    const onFinishTypingSlug = (value: string) => {
        setSlugError(!value.trim())
        if (autoSlug) {
            setAutoSlug(false)
        }
    }

    const onFinishTypingName = (value: string) => {
        setNameError(!value.trim())
    }

    const onNameChange = (value: string) => {
        setName(value)
        if (autoSlug) {
            const newSlug = value.toLowerCase().replace(/ /g, '_').replace(/[\W]+/g, '')
            setSlug(newSlug)
            setSlugError(!newSlug)
        }
    }

    return (
        <ModalDialog onDismiss={props.onCancel} interactive fromTop="20%">
            {props.loading && <ChartDataLoader />}
            <div style={{ width: '25em' }}>
                <ModalDialogTitle>{t('channels.addReseller.title')}</ModalDialogTitle>
                <FormItem htmlFor="reseller-name" style={{ position: 'relative' }}>
                    <FormItemName>{t('channels.addReseller.nameLabel')}</FormItemName>
                    <TextInput
                        id="reseller-name"
                        type="text"
                        placeholder={t('channels.addReseller.namePlaceholder')}
                        maxLength={100}
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        onFinishTyping={onFinishTypingName}
                        status={nameError ? 'error' : 'normal'}
                        style={{ width: '100%' }}
                    />
                    <ErrorMessage className={nameError ? 'validation-message-visible' : ''}>
                        {t('channels.addReseller.nameRequired')}
                    </ErrorMessage>
                </FormItem>
                <FormItem htmlFor="reseller-slug" style={{ position: 'relative' }}>
                    <FormItemName>{t('channels.addReseller.slugLabel')}</FormItemName>
                    <TextInput
                        id="reseller-slug"
                        type="text"
                        placeholder={t('channels.addReseller.slugPlaceholder')}
                        maxLength={100}
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        onFinishTyping={onFinishTypingSlug}
                        status={slugError ? 'error' : 'normal'}
                        style={{ width: '100%' }}
                    />
                    <ErrorMessage className={slugError ? 'validation-message-visible' : ''}>
                        {props.duplicateSlug
                            ? t('channels.addReseller.slugExists')
                            : t('channels.addReseller.slugRequired')}
                    </ErrorMessage>
                </FormItem>
                <NavigationSection>
                    <ActionButton size="large" secondary onClick={props.onCancel}>
                        {t('common.cancel')}
                    </ActionButton>
                    <ActionButton
                        id="add-new-reseller-button"
                        size="large"
                        style={{ marginLeft: '2em' }}
                        onClick={onSave}
                    >
                        {t('common.save')}
                    </ActionButton>
                </NavigationSection>
            </div>
        </ModalDialog>
    )
}
